import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Radar as RadarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PhaseChip, { getPhaseConfig } from '../PhaseChip';
import PhaseRadar from '../PhaseRadar';
import PhaseTimeline from '../PhaseTimeline';

const SIGNAL_TONE_STYLES = {
  positive: {
    backgroundColor: '#ecfdf5',
    borderColor: '#86efac',
    textColor: '#166534',
  },
  info: {
    backgroundColor: '#eff6ff',
    borderColor: '#93c5fd',
    textColor: '#1d4ed8',
  },
  accent: {
    backgroundColor: '#eef2ff',
    borderColor: '#a5b4fc',
    textColor: '#4338ca',
  },
  warning: {
    backgroundColor: '#fff7ed',
    borderColor: '#fdba74',
    textColor: '#c2410c',
  },
  negative: {
    backgroundColor: '#fef2f2',
    borderColor: '#fca5a5',
    textColor: '#b91c1c',
  },
  neutral: {
    backgroundColor: '#f8fafc',
    borderColor: '#cbd5e1',
    textColor: '#334155',
  },
};

const DETAIL_DATA_SHAPE = PropTypes.shape({
  ticker: PropTypes.string,
  company_name: PropTypes.string,
  sector_code: PropTypes.string,
  sector_name: PropTypes.string,
  trade_date: PropTypes.string,
  phase: PropTypes.string,
  confidence: PropTypes.number,
  confidence_level: PropTypes.string,
  error: PropTypes.string,
  scores: PropTypes.object,
  flags: PropTypes.object,
  features: PropTypes.object,
  signal_summary: PropTypes.shape({
    primary_key: PropTypes.string,
    secondary_key: PropTypes.string,
    extra_count: PropTypes.number,
    tone: PropTypes.string,
  }),
  freshness: PropTypes.shape({
    label_key: PropTypes.string,
    days_ago: PropTypes.number,
  }),
  interpretation: PropTypes.shape({
    snapshot: PropTypes.object,
    action_bias: PropTypes.object,
    insights: PropTypes.array,
  }),
});

function humanizeKey(key) {
  return key.replaceAll('_', ' ');
}

function getSignalLabel(t, key) {
  return t(`phase.table.signalSummary.${key}`, {
    defaultValue: humanizeKey(key),
  });
}

function createInsight(title, body, tone = 'neutral') {
  return { title, body, tone };
}

function getEvidenceToneStyles(tone = 'neutral') {
  return SIGNAL_TONE_STYLES[tone] || SIGNAL_TONE_STYLES.neutral;
}

function resolveTextSpec(t, spec) {
  if (!spec?.key) {
    return '';
  }

  const resolvedParams = Object.entries(spec.params || {}).reduce((acc, [key, value]) => {
    if (key.endsWith('_key') && typeof value === 'string') {
      acc[key.slice(0, -4)] = t(value);
      return acc;
    }
    if (key.endsWith('_keys') && Array.isArray(value)) {
      acc[key.slice(0, -5)] = value.map((item) => t(item)).join(', ');
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});

  return t(spec.key, resolvedParams);
}

function resolveEvidenceItems(t, evidenceItems) {
  if (!Array.isArray(evidenceItems)) {
    return [];
  }

  return evidenceItems
    .filter((item) => item?.label_key && item?.value)
    .map((item) => ({
      label: t(item.label_key, { defaultValue: item.label_key }),
      value: item.value,
      tone: item.tone || 'neutral',
    }));
}

function getConfidenceLabel(t, confidenceLevel, confidence) {
  if (confidenceLevel) {
    return t(`phase.table.confidenceLevels.${confidenceLevel}`, {
      defaultValue: humanizeKey(confidenceLevel),
    });
  }

  return t('phase.table.confidenceLevels.weak');
}

function getSnapshotSummary(stockDetail, t) {
  if (stockDetail?.interpretation?.snapshot) {
    return {
      title: resolveTextSpec(t, stockDetail.interpretation.snapshot.title),
      body: resolveTextSpec(t, stockDetail.interpretation.snapshot.body),
    };
  }

  const confidenceLabel = getConfidenceLabel(t, stockDetail?.confidence_level, stockDetail?.confidence || 0);
  const phaseLabel = stockDetail?.phase ? t(`phase.phases.${stockDetail.phase}`) : '—';
  const primarySignalLabel = stockDetail?.signal_summary?.primary_key
    ? getSignalLabel(t, stockDetail.signal_summary.primary_key)
    : t('phase.table.signalSummary.neutral');

  return {
    title: t('phase.detail.snapshotSummaryTitle', {
      phase: phaseLabel,
      confidence: confidenceLabel,
      defaultValue: `${phaseLabel} với độ tin cậy ${confidenceLabel}`,
    }),
    body: t('phase.detail.snapshotSummaryBody', {
      signal: primarySignalLabel,
      defaultValue: `Tín hiệu chi phối hiện tại là ${primarySignalLabel}, nên đây là điểm đọc nhanh để hiểu nên ưu tiên cơ hội hay phòng thủ.`,
    }),
  };
}

function buildBackendInsights(stockDetail, t) {
  if (Array.isArray(stockDetail?.interpretation?.insights) && stockDetail.interpretation.insights.length > 0) {
    return stockDetail.interpretation.insights.map((item) => createInsight(
      resolveTextSpec(t, item.title),
      resolveTextSpec(t, item.body),
      item.tone || 'neutral',
    ));
  }

  const signalSummary = stockDetail?.signal_summary || {};
  const activeFlags = Object.entries(stockDetail?.flags || {}).filter(([, value]) => Boolean(value));
  const insights = [];

  if (signalSummary.primary_key) {
    insights.push(createInsight(
      t('phase.detail.backendPrimarySignalTitle', { defaultValue: 'Tín hiệu backend chính' }),
      getSignalLabel(t, signalSummary.primary_key),
      signalSummary.tone || 'neutral',
    ));
  }

  if (signalSummary.secondary_key) {
    insights.push(createInsight(
      t('phase.detail.backendSecondarySignalTitle', { defaultValue: 'Tín hiệu backend phụ' }),
      getSignalLabel(t, signalSummary.secondary_key),
      'accent',
    ));
  }

  if (stockDetail?.confidence_level) {
    insights.push(createInsight(
      t('phase.detail.backendConfidenceTitle', { defaultValue: 'Mức tin cậy backend' }),
      getConfidenceLabel(t, stockDetail.confidence_level, stockDetail.confidence || 0),
      'info',
    ));
  }

  if (stockDetail?.freshness?.label_key && stockDetail.freshness.label_key !== 'unknown') {
    insights.push(createInsight(
      t('phase.detail.backendFreshnessTitle', { defaultValue: 'Độ mới backend' }),
      t(`phase.table.freshness.${stockDetail.freshness.label_key}`, { count: stockDetail.freshness.days_ago ?? 0 }),
      'neutral',
    ));
  }

  if (activeFlags.length > 0) {
    insights.push(createInsight(
      t('phase.detail.backendFlagsTitle', { defaultValue: 'Cờ backend đang bật' }),
      activeFlags.map(([key]) => t(`phase.flags.${key}`, { defaultValue: humanizeKey(key) })).join(', '),
      'warning',
    ));
  }

  return insights.slice(0, 6);
}

function formatMetricValue(value, formatter) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return formatter ? formatter(value) : value;
}

function MetricTile({ label, value, color = '#1e293b' }) {
  return (
    <Box>
      <Typography sx={{
        fontSize: '0.62rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: '#94a3b8',
        mb: 0.3,
      }}>
        {label}
      </Typography>
      <Typography sx={{
        fontSize: '0.92rem',
        fontWeight: 800,
        color,
        fontFamily: 'monospace',
        lineHeight: 1.2,
      }}>
        {value ?? '—'}
      </Typography>
    </Box>
  );
}

MetricTile.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
};

function SummaryStatCell({ label, value, tone = 'neutral' }) {
  const palette = getEvidenceToneStyles(tone);

  return (
    <Box sx={{ bgcolor: '#fff', px: 1.2, py: 1 }}>
      <Typography sx={{ fontSize: '0.56rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8' }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.94rem', fontWeight: 900, color: palette.textColor, lineHeight: 1.2, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}

SummaryStatCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  tone: PropTypes.string,
};

function InsightCard({ title, body, tone = 'neutral' }) {
  const palette = SIGNAL_TONE_STYLES[tone] || SIGNAL_TONE_STYLES.neutral;

  return (
    <Box sx={{
      borderRadius: '12px',
      border: `1px solid ${palette.borderColor}`,
      backgroundColor: palette.backgroundColor,
      p: 1.5,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: palette.textColor }}>
        {title}
      </Typography>
      <Typography sx={{ fontSize: '0.73rem', lineHeight: 1.5, color: '#475569' }}>
        {body}
      </Typography>
    </Box>
  );
}

InsightCard.propTypes = {
  title: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  tone: PropTypes.string,
};

function buildInvestorInsights(stockDetail, t) {
  return buildBackendInsights(stockDetail, t);
}

function StockPhaseDrawerContent({
  stockDetail,
  stockHistory,
  freshnessText,
  primarySignalLabel,
  secondarySignalLabel,
  signalTone,
  activeFlags,
  investorInsights,
  t,
}) {
  const snapshotSummary = getSnapshotSummary(stockDetail, t);
  const confidenceLabel = getConfidenceLabel(t, stockDetail.confidence_level, stockDetail.confidence || 0);
  const snapshotEvidence = resolveEvidenceItems(t, stockDetail?.interpretation?.snapshot?.evidence);
  const actionBias = stockDetail?.interpretation?.action_bias
    ? {
        label: resolveTextSpec(t, stockDetail.interpretation.action_bias.label),
        body: resolveTextSpec(t, stockDetail.interpretation.action_bias.body),
        tone: stockDetail.interpretation.action_bias.tone || 'neutral',
        evidence: resolveEvidenceItems(t, stockDetail.interpretation.action_bias.evidence),
      }
    : null;
  const actionTone = SIGNAL_TONE_STYLES[actionBias?.tone] || SIGNAL_TONE_STYLES.neutral;
  const summaryStats = [
    {
      label: t('phase.detail.phase'),
      value: t(`phase.phases.${stockDetail.phase}`),
      tone: 'info',
    },
    {
      label: t('phase.detail.confidence'),
      value: `${(stockDetail.confidence * 100).toFixed(0)}%`,
      tone: stockDetail.confidence >= 0.5 ? 'positive' : 'warning',
    },
    {
      label: t('phase.detail.signalFocusTitle', { defaultValue: 'Điểm cần chú ý' }),
      value: primarySignalLabel || t('phase.table.signalSummary.neutral'),
      tone: stockDetail?.signal_summary?.tone || 'neutral',
    },
    ...snapshotEvidence.slice(0, 3),
  ].filter((item) => item?.value);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 0.5 }}>
      <Box sx={{
        backgroundColor: '#fff',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Box sx={{ px: 1.5, py: 1.15, borderBottom: '1px solid #e2e8f0', bgcolor: '#fcfdff' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.25, flexWrap: 'wrap' }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '0.64rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {t('phase.detail.snapshot', { defaultValue: 'Phase snapshot' })}
            </Typography>
              <Typography sx={{ fontSize: '0.98rem', fontWeight: 900, color: '#0f172a', mt: 0.35, lineHeight: 1.25 }}>
              {snapshotSummary.title}
            </Typography>
              <Typography sx={{ fontSize: '0.76rem', color: '#475569', mt: 0.45, lineHeight: 1.5, maxWidth: 520 }}>
              {snapshotSummary.body}
            </Typography>
              <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap', mt: 0.7 }}>
                {primarySignalLabel && (
                  <Chip
                    label={primarySignalLabel}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.64rem',
                      fontWeight: 800,
                      backgroundColor: signalTone.backgroundColor,
                      color: signalTone.textColor,
                      border: `1px solid ${signalTone.borderColor}`,
                      borderRadius: '999px',
                    }}
                  />
                )}
                {(stockDetail.sector_name || stockDetail.sector_code) && (
                  <Chip
                    label={stockDetail.sector_name || stockDetail.sector_code}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.64rem',
                      fontWeight: 700,
                      backgroundColor: '#f8fafc',
                      color: '#475569',
                      border: '1px solid #cbd5e1',
                      borderRadius: '999px',
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box sx={{ px: 1.5, py: 1.25, bgcolor: actionTone.backgroundColor, borderBottom: `1px solid ${actionTone.borderColor}` }}>
          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: actionTone.textColor, textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.35 }}>
              {t('phase.detail.actionBiasTitle', { defaultValue: 'Ưu tiên hành động' })}
            </Typography>
          <Typography sx={{ fontSize: '0.94rem', fontWeight: 900, color: actionTone.textColor, lineHeight: 1.25 }}>
              {actionBias?.label || confidenceLabel}
            </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#475569', mt: 0.35, lineHeight: 1.5 }}>
              {actionBias?.body || t('phase.detail.backendConfidenceBody', {
                value: `${(stockDetail.confidence * 100).toFixed(0)}%`,
                defaultValue: `API currently classifies this snapshot at ${stockDetail.confidence * 100}% confidence.`,
              })}
            </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '1px', bgcolor: '#e2e8f0', borderTop: '1px solid #e2e8f0' }}>
          {summaryStats.map((item) => (
            <SummaryStatCell
              key={`${item.label}-${item.value}`}
              label={item.label}
              value={item.value}
              tone={item.tone}
            />
          ))}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', px: 1.5, py: 1 }}>
          {secondarySignalLabel && (
            <Chip
              label={secondarySignalLabel}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.65rem',
                fontWeight: 700,
                backgroundColor: '#eef2ff',
                color: '#4338ca',
                border: '1px solid #c7d2fe',
              }}
            />
          )}
          {stockDetail.signal_summary?.extra_count > 0 && (
            <Chip
              label={t('phase.detail.additionalSignals', {
                count: stockDetail.signal_summary.extra_count,
                defaultValue: `+${stockDetail.signal_summary.extra_count} more signals`,
              })}
              size="small"
              sx={{
                height: 24,
                fontSize: '0.65rem',
                fontWeight: 700,
                backgroundColor: '#f8fafc',
                color: '#475569',
                border: '1px solid #cbd5e1',
              }}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '220px 1fr' }, gap: 2.5 }}>
        <Box sx={{
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          p: 2.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <PhaseRadar scores={stockDetail.scores} size={190} />
        </Box>

        <Box sx={{
          background: '#fff',
          borderRadius: '16px',
          border: '1px solid #e2e8f0',
          p: 2.25,
        }}>
          <Typography sx={{
            fontSize: '0.68rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#94a3b8',
            mb: 2,
          }}>
            {t('phase.detail.marketStructure', { defaultValue: 'Market structure' })}
          </Typography>

          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
            gap: 2,
          }}>
            <MetricTile
              label={t('phase.detail.phase')}
              value={t(`phase.phases.${stockDetail.phase}`)}
              color={getPhaseConfig(stockDetail.phase).color}
            />
            <MetricTile
              label={t('phase.detail.confidence')}
              value={`${(stockDetail.confidence * 100).toFixed(0)}%`}
              color={stockDetail.confidence >= 0.5 ? '#10b981' : '#f59e0b'}
            />
            <MetricTile label={t('phase.detail.date')} value={stockDetail.trade_date} color="#64748b" />

            <MetricTile
              label={t('phase.detail.atrContract')}
              value={formatMetricValue(stockDetail.features?.atr_contract, (value) => value.toFixed(2))}
              color="#64748b"
            />
            <MetricTile
              label={t('phase.detail.volRatio')}
              value={formatMetricValue(stockDetail.features?.vol_ratio, (value) => value.toFixed(2))}
              color="#64748b"
            />
            <MetricTile
              label={t('phase.detail.volSpike')}
              value={formatMetricValue(stockDetail.features?.vol_spike, (value) => value.toFixed(2))}
              color="#64748b"
            />
            <MetricTile
              label={t('phase.detail.slopeFast')}
              value={formatMetricValue(stockDetail.features?.slope_fast, (value) => value.toFixed(0))}
              color="#64748b"
            />
            <MetricTile
              label={t('phase.detail.slopeSlow', { defaultValue: 'Slope Slow' })}
              value={formatMetricValue(stockDetail.features?.slope_slow, (value) => value.toFixed(0))}
              color="#64748b"
            />
            <MetricTile
              label={t('phase.detail.closePos')}
              value={formatMetricValue(stockDetail.features?.close_pos, (value) => value.toFixed(2))}
              color="#64748b"
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography sx={{
            fontSize: '0.68rem',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#94a3b8',
            mb: 1,
          }}>
            {t('phase.flags.title')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {activeFlags.map(([key]) => (
              <Chip
                key={key}
                label={t(`phase.flags.${key}`, { defaultValue: humanizeKey(key) })}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  backgroundColor: ['spring_reclaim', 'absorption_day'].includes(key) ? '#ecfdf5' : '#fff7ed',
                  color: ['spring_reclaim', 'absorption_day'].includes(key) ? '#166534' : '#c2410c',
                  border: `1px solid ${['spring_reclaim', 'absorption_day'].includes(key) ? '#86efac' : '#fdba74'}`,
                  borderRadius: '6px',
                }}
              />
            ))}
            {activeFlags.length === 0 && (
              <Typography sx={{ fontSize: '0.72rem', color: '#cbd5e1' }}>
                {t('phase.flags.none')}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{
        background: '#fff',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        p: 2.25,
      }}>
        <Typography sx={{
          fontSize: '0.68rem',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#94a3b8',
          mb: 2,
        }}>
          {t('phase.detail.investorInsights', { defaultValue: 'Investor insights' })}
        </Typography>

        {investorInsights.length > 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 1.25 }}>
            {investorInsights.map((insight, index) => (
              <InsightCard
                key={`${insight.title}-${index}`}
                title={insight.title}
                body={insight.body}
                tone={insight.tone}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.78rem', color: '#94a3b8' }}>
            {t('phase.detail.noInsights', { defaultValue: 'No additional insights available for this snapshot.' })}
          </Typography>
        )}
      </Box>

      {stockHistory && stockHistory.length > 0 && (
        <PhaseTimeline history={stockHistory} />
      )}
    </Box>
  );
}

StockPhaseDrawerContent.propTypes = {
  stockDetail: DETAIL_DATA_SHAPE.isRequired,
  stockHistory: PropTypes.array,
  freshnessText: PropTypes.string,
  primarySignalLabel: PropTypes.string,
  secondarySignalLabel: PropTypes.string,
  signalTone: PropTypes.shape({
    backgroundColor: PropTypes.string,
    borderColor: PropTypes.string,
    textColor: PropTypes.string,
  }).isRequired,
  activeFlags: PropTypes.arrayOf(PropTypes.array).isRequired,
  investorInsights: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    tone: PropTypes.string,
  })).isRequired,
  t: PropTypes.func.isRequired,
};

export default function StockPhaseDetailDialog({
  open,
  onClose,
  stockDetail,
  stockHistory,
  isLoading,
  fallbackTicker,
}) {
  const { t } = useTranslation();
  const phaseConfig = stockDetail?.phase ? getPhaseConfig(stockDetail.phase) : null;
  const signalTone = SIGNAL_TONE_STYLES[stockDetail?.signal_summary?.tone] || SIGNAL_TONE_STYLES.neutral;
  const investorInsights = buildInvestorInsights(stockDetail, t);
  const activeFlags = stockDetail?.flags
    ? Object.entries(stockDetail.flags).filter(([, value]) => value)
    : [];
  const freshnessText = stockDetail?.freshness && stockDetail.freshness.label_key !== 'unknown'
    ? t(`phase.table.freshness.${stockDetail.freshness.label_key}`, { count: stockDetail.freshness.days_ago ?? 0 })
    : stockDetail?.trade_date;
  const primarySignalLabel = stockDetail?.signal_summary?.primary_key
    ? getSignalLabel(t, stockDetail.signal_summary.primary_key)
    : null;
  const secondarySignalLabel = stockDetail?.signal_summary?.secondary_key
    ? getSignalLabel(t, stockDetail.signal_summary.secondary_key)
    : null;
  const drawerTitleTicker = stockDetail?.ticker || fallbackTicker?.toUpperCase() || '—';
  const rawCompanyName = (stockDetail?.company_name || '').trim();
  const sectorLabel = stockDetail?.sector_name || stockDetail?.sector_code || '';
  const companyDisplayName = rawCompanyName && rawCompanyName.toUpperCase() !== drawerTitleTicker
    ? rawCompanyName
    : '';
  const drawerTitleName = companyDisplayName || sectorLabel || drawerTitleTicker;
  const drawerSubtitleParts = [
    sectorLabel && sectorLabel !== drawerTitleName ? sectorLabel : null,
    stockDetail?.trade_date,
  ].filter(Boolean);

  let content = null;
  if (isLoading) {
    content = (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Box sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          background: phaseConfig?.gradient || 'linear-gradient(135deg, #667eea, #764ba2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2,
          animation: 'pulse 1.5s ease-in-out infinite',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.5 },
          },
        }}>
          <RadarIcon sx={{ color: '#fff', fontSize: 24 }} />
        </Box>
        <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
          {t('phase.detail.analyzing')}
        </Typography>
      </Box>
    );
  } else if (stockDetail?.error) {
    content = <Alert severity="error" sx={{ my: 2, borderRadius: '12px' }}>{stockDetail.error}</Alert>;
  } else if (stockDetail) {
    content = (
      <StockPhaseDrawerContent
        stockDetail={stockDetail}
        stockHistory={stockHistory}
        freshnessText={freshnessText}
        primarySignalLabel={primarySignalLabel}
        secondarySignalLabel={secondarySignalLabel}
        signalTone={signalTone}
        activeFlags={activeFlags}
        investorInsights={investorInsights}
        t={t}
      />
    );
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      slotProps={{
        paper: {
          sx: {
            width: { xs: '100%', sm: 560, lg: 640 },
            maxWidth: '100vw',
            borderLeft: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: '#fff',
        borderBottom: '1px solid #e2e8f0',
        py: 1.1,
        px: 2,
        boxShadow: '0 1px 0 rgba(15, 23, 42, 0.04)',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0, flex: 1 }}>
          <Box sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 8px 20px rgba(15,23,42,0.12)',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}>
            <Box sx={{ flex: 1, width: '100%', bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '0.98rem', fontWeight: 900, color: '#fff', lineHeight: 1, letterSpacing: '-0.02em' }}>
                {drawerTitleTicker}
              </Typography>
            </Box>
            <Box sx={{ height: 16, width: '100%', background: phaseConfig?.gradient || '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 800, color: '#fff', letterSpacing: '0.12em', lineHeight: 1 }}>
                PHS
              </Typography>
            </Box>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '1.08rem', fontWeight: 900, color: '#0f172a', lineHeight: 1.12, letterSpacing: '-0.025em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {drawerTitleName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', mt: 0.45 }}>
              {drawerSubtitleParts.map((part, index) => (
                <React.Fragment key={`${part}-${index}`}>
                  {index > 0 && (
                    <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: '#cbd5e1', flexShrink: 0 }} />
                  )}
                  <Typography sx={{ fontSize: '0.72rem', color: index === 0 ? '#475569' : '#64748b', fontWeight: index === 0 ? 700 : 500, lineHeight: 1.2 }}>
                    {part}
                  </Typography>
                </React.Fragment>
              ))}
            </Box>
          </Box>
          {stockDetail?.phase && !isLoading && (
            <Box sx={{ ml: 0.5, flexShrink: 0 }}>
              <PhaseChip phase={stockDetail.phase} confidence={stockDetail.confidence} />
            </Box>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: '14px',
            width: 42,
            height: 42,
            color: '#475569',
            backgroundColor: '#fff',
            boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
            ml: 1,
            '&:hover': { backgroundColor: '#f8fafc', borderColor: '#94a3b8' },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: '#f8fafc', overflowY: 'auto', flex: 1 }}>
        {content}
      </Box>
    </Drawer>
  );
}

StockPhaseDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  stockDetail: DETAIL_DATA_SHAPE,
  stockHistory: PropTypes.array,
  isLoading: PropTypes.bool,
  fallbackTicker: PropTypes.string,
};